import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["badge", "children"],
  _excluded2 = ["defaultOpen"],
  _excluded3 = ["to"];
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import I18n from 'I18n';
import H2 from 'UIComponents/elements/headings/H2';
import UISideNavItem from 'UIComponents/nav/UISideNavItem';
import UIRouterSideNav from 'ui-addon-react-router-dom/UIRouterSideNav';
import { BATTLESHIP, CALYPSO, CALYPSO_LIGHT, GYPSUM, OLAF } from 'HubStyleTokens/colors';
import { mediaQueries } from 'developer-docs-shared-components/utils/mediaQueries';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UILink from 'UIComponents/link/UILink';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import UIRouterNavLink from 'ui-addon-react-router-dom/UIRouterNavLink';
import UINavListItem from 'UIComponents/nav/UINavListItem';
import { SideNavTitleWithStatusBadge } from './NavConfigListItem';
import useLocale from 'developer-docs-shared-components/hooks/useLocale';
import { getLocalizedBadge, isUrlLocalized } from '../utils/localization';
import { getEnglishBaseUrl, isVersionedPath } from '../utils/urls';
import { isDocGatedAndDoesUserNotHaveAccess } from '../utils/gates';
import { SECONDARY_NAV_WIDTH } from '../utils/css';
import { useSelector } from 'react-redux';
import { selectUserGates } from '../selectors/userState';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
/**
 * The width, in pixels, of the inner wrapper when the navigation is
 * collapsed at desktop. Represents the size of the interactive area that can
 * be clicked to expand the menu.
 */
const COLLAPSED_WIDTH = 24;

/**
 * The width, in pixels, of the inner wrapper when the navigation is
 * expanded at desktop. 20px narrower than the outer secondary nav wrapper.
 */
const EXPANDED_WIDTH = SECONDARY_NAV_WIDTH - 20;

/**
 * The section header above the `<nav />`. Describes the nav below,
 * visually and as a heading.
 */
const SectionHeader = styled(props => /*#__PURE__*/_jsx(H2, Object.assign({}, props))).withConfig({
  displayName: "SecondaryNav__SectionHeader",
  componentId: "sc-181bkap-0"
})(["background-color:", ";padding:19px 60px 19px 32px;margin-bottom:24px;font-size:20px;font-weight:300;"], GYPSUM);

/**
 * The router-aware linked section header (e.g. `API Endpoints`).
 */
const SectionHeaderLink = styled(_ref => {
  let {
      badge,
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (badge) {
    // Add badge wrapper
    return /*#__PURE__*/_jsxs(UIRouterNavLink, Object.assign({}, rest, {
      children: [/*#__PURE__*/_jsx(View, {
        marginInlineEnd: "200",
        as: "span",
        children: children
      }), /*#__PURE__*/_jsx("span", {
        className: "badge-wrapper",
        children: getLocalizedBadge(badge)
      })]
    }));
  }
  return /*#__PURE__*/_jsx(UIRouterNavLink, Object.assign({}, rest, {
    children: children
  }));
}).withConfig({
  displayName: "SecondaryNav__SectionHeaderLink",
  componentId: "sc-181bkap-1"
})(["display:block;transition:background-color 150ms ease-out;padding:7px 0 7px 13px;&.SectionHeaderLink-active{background-color:", ";position:relative;font-weight:500;", "{&:hover{font-weight:500;}}&::before{content:'';border-left:4px solid ", ";height:100%;left:0;position:absolute;top:0;border-radius:3px;}}", "{&:hover{background-color:", ";}}.badge-wrapper{position:relative;bottom:3px;margin-right:2px;}"], CALYPSO_LIGHT, mediaQueries.hover, CALYPSO, mediaQueries.hover, CALYPSO_LIGHT);
const Nav = styled(UIRouterSideNav).withConfig({
  displayName: "SecondaryNav__Nav",
  componentId: "sc-181bkap-2"
})(["padding:0;margin-top:0;"]);

/**
 * Shared styles for all top-level links and folders.
 */
const topLevelChildStyles = css(["padding:10.5px 20px 10.5px 45px;width:100%;"]);

/**
 * Shared styles for router-aware and non-router-aware top-level links.
 */
const topLevelLinkStyles = css(["", " left:0;position:relative;> span{font-size:16px;}"], topLevelChildStyles);

/**
 * Folders (e.g. `CMS`) that share some styles with top-level links at
 * desktop.
 */
const TopLevelFolder = styled(UILink).withConfig({
  displayName: "SecondaryNav__TopLevelFolder",
  componentId: "sc-181bkap-3"
})(["", " position:static;"], topLevelChildStyles);

/**
 * Top-level router-aware links (e.g. `Files`) that share the same style as
 * top-level categories at desktop.
 */
const TopLevelRouterLink = styled(UIRouterNavLink).withConfig({
  displayName: "SecondaryNav__TopLevelRouterLink",
  componentId: "sc-181bkap-4"
})(["", ""], topLevelLinkStyles);

/**
 * Top-level links (e.g. `Files`) that share the same style as top-level
 * categories at desktop.
 */
const TopLevelLink = styled(UILink).withConfig({
  displayName: "SecondaryNav__TopLevelLink",
  componentId: "sc-181bkap-5"
})(["", ""], topLevelLinkStyles);

/**
 * A top-level folder's accordion header used for categories (e.g. `CMS`).
 */
const FolderSideNavItem = _ref2 => {
  let {
      defaultOpen = false
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const [open, setOpen] = useState(defaultOpen);
  return /*#__PURE__*/_jsx(UISideNavItem, Object.assign({}, rest, {
    linkComponent: TopLevelFolder,
    open: open,
    iconRight: /*#__PURE__*/_jsx(UIIcon, {
      name: open ? 'minus' : 'add',
      size: 10
    }),
    onOpenChange: ({
      target: {
        value: isAccordionOpen
      }
    }) => {
      setOpen(isAccordionOpen);
    }
  }));
};

/**
 * Adjusts the font size for an expandable top-level folder (e.g. `CMS`).
 */
const FolderSideNavItemTitle = styled.span.withConfig({
  displayName: "SecondaryNav__FolderSideNavItemTitle",
  componentId: "sc-181bkap-6"
})(["font-size:16px;"]);

/**
 * A link in the side nav.
 */
const Link = styled(_ref3 => {
  let {
      to
    } = _ref3,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  return /*#__PURE__*/_jsx(UISideNavItem, Object.assign({}, rest, {
    to: to // when to is undefined shouldn't matter since rest.href is passed along
    ,
    linkComponent: to ? UIRouterNavLink : undefined // unset when non-router-aware so UISideNavItem.defaultProps value is used
  }));
}).withConfig({
  displayName: "SecondaryNav__Link",
  componentId: "sc-181bkap-7"
})(["padding-right:8px;width:86%;margin-left:14%;"]);
const Wrapper = styled.div.withConfig({
  displayName: "SecondaryNav__Wrapper",
  componentId: "sc-181bkap-8"
})(["background-color:", ";transition:flex 0.2s ease-in-out;flex:0 0 ", "px;height:100%;overflow-x:hidden;overflow-y:", ";"], OLAF, ({
  isOpen
}) => isOpen ? SECONDARY_NAV_WIDTH : COLLAPSED_WIDTH, ({
  isOpen
}) => isOpen ? 'auto' : 'hidden');
const ArrowButton = styled(UIIconButton).withConfig({
  displayName: "SecondaryNav__ArrowButton",
  componentId: "sc-181bkap-9"
})(["position:absolute;right:0;top:0;z-index:1;transition:0.2s ease-in-out background-color;> span{position:absolute;top:24px;right:20px;}", ""], ({
  open
}) => {
  if (!open) {
    return `
        height: 100%;
        && {
          width: 100%;
          background-color: ${BATTLESHIP};
        }
        > span {
          right: 4px;
        }
      `;
  }
  return null;
});
const InnerWrapper = styled.div.withConfig({
  displayName: "SecondaryNav__InnerWrapper",
  componentId: "sc-181bkap-10"
})(["position:relative;width:100%;height:100%;min-width:", "px;"], ({
  isOpen
}) => isOpen ? EXPANDED_WIDTH : COLLAPSED_WIDTH);
const removeLastElementFromUrl = url => {
  return url.slice(0, url.lastIndexOf('/'));
};

/**
 * Determines whether this link is active. If this is a versioned url, remove
 * the version from the pathname before comparing.
 */
const isActiveItem = ({
  url
}, pathname) => {
  const isVersioned = url.includes('/reference/api') && isVersionedPath(pathname);
  return url === pathname || isVersioned && url === removeLastElementFromUrl(pathname);
};
const containsFocusedItem = (items, pathname) => items.some(item => {
  if ('items' in item) {
    return containsFocusedItem(item.items, pathname);
  }
  return isActiveItem(item, pathname);
});
const generateSideNav = (items, pathname, locale, userGates) => {
  return items.map(item => {
    const sharedProps = {
      key: `${pathname}${item.label}`
    };
    if (isDocGatedAndDoesUserNotHaveAccess(item, userGates)) {
      return null;
    }
    if ('items' in item) {
      const children = generateSideNav(item.items, pathname, locale, userGates);
      let open = containsFocusedItem(item.items, pathname);
      // Second-level folder (an `a[role="button"]` — has children but no href)
      return (
        /*#__PURE__*/
        /* eslint-disable-next-line react/jsx-key */
        _jsx(Link, Object.assign({}, sharedProps, {
          title: item.label,
          defaultOpen: open,
          iconRight: () => {
            if (children !== null) {
              return /*#__PURE__*/_jsx(UIIcon, {
                name: open ? 'minus' : 'add',
                size: 10
              });
            }
            return null;
          },
          onOpenChange: ({
            target: {
              value: isAccordionOpen
            }
          }) => {
            // Needed to refresh +/- icon on second-level folders at desktop (e.g. +/- icon to right of CMS > Blogs folder)
            open = isAccordionOpen;
          },
          children: generateSideNav(item.items, pathname, locale, userGates)
        }))
      );
    } else {
      const isLocalized = isUrlLocalized(item.url, locale);
      const navItemTitle = !isLocalized || item.badge ? /*#__PURE__*/_jsx(SideNavTitleWithStatusBadge, {
        item: item,
        isLocalized: isLocalized
      }) : item.label;
      if (item.url.startsWith('/')) {
        // Internal link

        if (!isLocalized) {
          // This link does not exist in the current locale, so link should point to en-us version
          const englishUrl = getEnglishBaseUrl(item.url);
          return (
            /*#__PURE__*/
            /* eslint-disable-next-line react/jsx-key */
            _jsx(Link, Object.assign({}, sharedProps, {
              title: navItemTitle,
              href: englishUrl,
              hrefLang: "en-us",
              external: true
            }))
          );
        }

        // Internal link within the same locale
        return (
          /*#__PURE__*/
          /* eslint-disable-next-line react/jsx-key */
          _jsx(Link, Object.assign({}, sharedProps, {
            title: navItemTitle,
            to: item.url,
            active: isActiveItem(item, pathname)
          }))
        );
      } else {
        // External link
        return (
          /*#__PURE__*/
          /* eslint-disable-next-line react/jsx-key */
          _jsx(Link, Object.assign({}, sharedProps, {
            title: navItemTitle,
            href: item.url,
            external: true
          }))
        );
      }
    }
  });
};
function SecondaryNav({
  navConfig
}) {
  const {
    pathname
  } = useLocation();
  const [open, setOpen] = useState(true);
  const navHeadingId = useUniqueId('nav-heading-');
  const locale = useLocale();
  const userGates = useSelector(selectUserGates);
  const navChildren = navConfig.items.map(item => {
    // If gate exists, and check fails, return null
    if (isDocGatedAndDoesUserNotHaveAccess(item, userGates.gates)) {
      return null;
    }
    if ('items' in item) {
      // NavFolder - Expandable section
      const sideNavItems = generateSideNav(item.items, pathname, locale, userGates.gates);
      if (!sideNavItems.length) return null;
      return /*#__PURE__*/_jsx(FolderSideNavItem, {
        title: /*#__PURE__*/_jsx(FolderSideNavItemTitle, {
          children: item.label
        }),
        defaultOpen: containsFocusedItem(item.items, pathname),
        children: sideNavItems
      }, `${pathname}${item.label}`);
    }

    // NavPage - top-level link like OAuth
    const isLocalized = isUrlLocalized(item.url, locale);
    const localizationProps = {
      linkComponent: isLocalized ? TopLevelRouterLink : TopLevelLink,
      external: !isLocalized,
      hrefLang: !isLocalized ? 'en-us' : undefined,
      title: !isLocalized || item.badge ? /*#__PURE__*/_jsx(SideNavTitleWithStatusBadge, {
        item: item,
        isLocalized: isLocalized
      }) : /*#__PURE__*/_jsx(FolderSideNavItemTitle, {
        children: item.label
      })
    };
    if (isLocalized) {
      localizationProps.to = item.url;
    } else {
      localizationProps.href = getEnglishBaseUrl(item.url);
    }
    return /*#__PURE__*/_jsx(UINavListItem, Object.assign({
      // If versioning is enabled and this is a versioned url, remove the version from the pathname before comparing
      active: isActiveItem(item, pathname)
    }, localizationProps), `${pathname}${item.label}`);
  });
  return /*#__PURE__*/_jsx(Wrapper, {
    isOpen: open,
    "data-test-id": open ? `${navConfig.locatorPageType}_SIDE_NAV_OPEN` : `${navConfig.locatorPageType}_SIDE_NAV_COLLAPSED`,
    children: /*#__PURE__*/_jsxs(InnerWrapper, {
      isOpen: open,
      children: [/*#__PURE__*/_jsx(ArrowButton, {
        use: "unstyled",
        onClick: () => setOpen(!open),
        open: open,
        children: /*#__PURE__*/_jsx(UIIcon, {
          size: 16,
          name: open ? 'first' : 'last',
          screenReaderText: I18n.text('developer-docs-ui.accessibility.toggleMenuIcons')
        })
      }), open && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(SectionHeader, {
          "aria-level": null,
          id: navHeadingId,
          children: /*#__PURE__*/_jsx(SectionHeaderLink, {
            to: navConfig.commonPath,
            use: "unstyled",
            exact: true,
            badge: navConfig.badge,
            activeClassName: "SectionHeaderLink-active",
            children: navConfig.sectionLabel
          })
        }), /*#__PURE__*/_jsx(Nav, {
          value: pathname,
          "aria-describedby": navHeadingId,
          "aria-label": navConfig.sectionLabel,
          children: navChildren
        })]
      })]
    })
  });
}
export default SecondaryNav;